<script lang="ts">
	import { superForm, defaults } from "sveltekit-superforms";
	import { zod4 } from "sveltekit-superforms/adapters";
	import { salesSlugContactFormClientSchema } from "@fergmar/contracts/public/forms/sales-slug-contact-form.schema";
	import { trpcBrowser } from "#trpc/client.svelte.js";
	import { formatPhoneNumber } from "#lib/web-ui/helpers.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import { Textarea } from "#lib/web-ui/ui/textarea/index.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { toast } from "svelte-sonner";
	import { page } from "$app/state";
	import { dev } from "$app/env";
	import { TurnstileField } from "#lib/web-ui/forms/turnstile/index.js";
	import type { PublicInvestmentListing } from "@fergmar/contracts/public/investment-listings";

	type PublicListing = PublicInvestmentListing;

	let { listing }: { listing: PublicListing } = $props();

	const address = $derived(
		`${listing.addressStreet1}${listing.addressStreet2 ? `, ${listing.addressStreet2}` : ""}, ${listing.addressCity}, ${listing.addressState} ${listing.addressZip}`
	);

	function pushToDataLayer(eventName: string) {
		if (typeof window !== "undefined" && window.dataLayer) {
			window.dataLayer.push({
				event: eventName,
				formId: "sales_slug_contact_form",
				pagePath: window.location.pathname
			});
		}
	}

	const form = superForm(
		defaults(
			{ message: `I have questions about the property at ${address}` },
			zod4(salesSlugContactFormClientSchema)
		),
		{
			id: `sales-contact-${listing.slug}`,
			SPA: true,
			resetForm: true,
			validators: zod4(salesSlugContactFormClientSchema),
			async onUpdate({ form }) {
				if (!form.valid) return;

				try {
					await trpcBrowser.public.forms.submitSalesSlugContact.mutate({
						...form.data,
						listingSlug: listing.slug,
						listingAddress: address,
						listingStatus: listing.status,
						listingPrice: listing.currentPrice
							? `$${Math.round(listing.currentPrice).toLocaleString()}`
							: "N/A",
						pageUrl: page.url.href,
						routeId: page.route.id ?? "",
						utmSource: page.url.searchParams.get("utm_source") ?? "",
						utmMedium: page.url.searchParams.get("utm_medium") ?? "",
						utmCampaign: page.url.searchParams.get("utm_campaign") ?? "",
						utmContent: page.url.searchParams.get("utm_content") ?? "",
						utmTerm: page.url.searchParams.get("utm_term") ?? ""
					});
				} catch (e) {
					form.data.turnstileToken = "";
					toast.error(
						e instanceof Error ? e.message : "Error submitting, please try again later."
					);
					pushToDataLayer("form_submission_error");
					return;
				}

				toast.success("Message sent successfully!");
				pushToDataLayer("form_submission_success");
			}
		}
	);

	const { form: formData, enhance, errors, submitting } = form;

	if (dev) {
		$formData.name = "John Doe";
		$formData.phone = "(123) 456-7890";
		$formData.email = "john.doe@example.com";
	}
</script>

<form use:enhance method="POST" class="w-full space-y-4">
	<div class="space-y-2">
		<Label for="name">Name</Label>
		<Input id="name" name="name" type="text" bind:value={$formData.name} autocomplete="name" />
		{#if $errors.name}
			<p class="text-destructive text-sm">{$errors.name}</p>
		{/if}
	</div>

	<div class="space-y-2">
		<Label for="phone">Phone</Label>
		<Input
			id="phone"
			name="phone"
			type="tel"
			bind:value={$formData.phone}
			placeholder="(123) 456-7890"
			autocomplete="tel"
			onchange={(e) => {
				$formData.phone = formatPhoneNumber(e.currentTarget.value);
			}}
		/>
		{#if $errors.phone}
			<p class="text-destructive text-sm">{$errors.phone}</p>
		{/if}
	</div>

	<div class="space-y-2">
		<Label for="email">Email</Label>
		<Input
			id="email"
			name="email"
			type="email"
			bind:value={$formData.email}
			autocomplete="email"
			onchange={(e) => {
				$formData.email = e.currentTarget.value.toLowerCase();
			}}
		/>
		{#if $errors.email}
			<p class="text-destructive text-sm">{$errors.email}</p>
		{/if}
	</div>

	<div class="space-y-2">
		<Label for="message">Message</Label>
		<Textarea
			id="message"
			name="message"
			class="w-full bg-white"
			bind:value={$formData.message}
		/>
		{#if $errors.message}
			<p class="text-destructive text-sm">{$errors.message}</p>
		{/if}
	</div>

	<div>
		<TurnstileField bind:token={$formData.turnstileToken} class="mb-4" />
	</div>

	<Button type="submit" disabled={$submitting} aria-busy={$submitting} class="w-full">
		{$submitting ? "Submitting..." : "Submit"}
	</Button>
</form>
